import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomersAPI, { Customer, CustomerSortBy, CustomerSortDirection } from "../api/CustomersAPI";
import { toastService } from "../services/ToastService";
import { customerMatchesListFilters } from "../shared/customerListFilters";
import { CustomerStatus } from "../shared/customerStatusToTranslationKey";

export type Filters = {
    employee?: string;
    employeeIds?: string[];
    page?: number;
    size?: number;
    status?: string;
    statuses?: CustomerStatus[];
    searchText?: string;
    weddingMonth?: string;
    sortBy?: CustomerSortBy;
    sortDirection?: CustomerSortDirection;
};

type PaginationInfo = {
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};

type ReturnedProps = {
  isLoading: boolean,
  customers: Customer[],
  pagination: PaginationInfo,
  addFn: (customer: Customer) => Promise<any>,
  updateFn: (customer: Customer, options?: { reload?: boolean }) => Promise<any>,
  removeFn: (customer: Customer) => Promise<any>,
  setPage: (page: number) => void,
  setPageSize: (size: number) => void,
};

export function useGetCustomers({ employee, employeeIds, page = 0, size = 50, status, statuses, searchText, weddingMonth, sortBy, sortDirection }: Filters): ReturnedProps {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalElements: 0,
    totalPages: 0,
    size: 50,
    number: 0
  });
  const [currentPage, setCurrentPage] = useState(page);
  const [currentPageSize, setCurrentPageSize] = useState(size);

  const load = useCallback(() => {
    setIsLoading(true);
    CustomersAPI.get({ employee, employeeIds, page: currentPage, size: currentPageSize, status, statuses, searchText, weddingMonth, sortBy, sortDirection })
      .then(response => {
        setCustomers(response.data.content);
        setPagination({
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          size: response.data.size,
          number: response.data.number
        });
      })
      .catch(() => {
        toastService.showToast(t("toast.errors.customers.load"), 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, employeeIds, currentPage, currentPageSize, status, statuses, searchText, weddingMonth, sortBy, sortDirection]);

  useEffect(() => {
    load();
  }, [load]);

  const addFn = async (customer: Customer) => {
    try {
      await CustomersAPI.add(customer);
      load();
    } catch (err) {
      toastService.showToast(t("toast.errors.customers.add"), 'error');
    }
  };

  const updateFn = async (customer: Customer, { reload = true }: { reload?: boolean } = {}) => {
    try {
      const response = await CustomersAPI.update(customer.id!, customer);
      if (reload) {
        load();
      } else {
        const listFilters = { statuses, employeeIds };
        setCustomers(prevCustomers =>
          prevCustomers
            .map(existingCustomer =>
              String(existingCustomer.id) === String(response.data.id)
                ? response.data
                : existingCustomer,
            )
            .filter(updatedCustomer => customerMatchesListFilters(updatedCustomer, listFilters)),
        );
      }
      return response;
    } catch (err) {
      toastService.showToast(t("toast.errors.customers.update"), 'error');
    }
  };

  const removeFn = async (customer: Customer) => {
    try {
      await CustomersAPI.remove(customer.id!);
      load();
    } catch (err) {
      toastService.showToast(t("toast.errors.customers.remove"), 'error');
    }
  };

  const setPage = (page: number) => {
    setCurrentPage(page);
  };

  const setPageSize = (size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(0);
  };

  return { isLoading, customers, pagination, addFn, updateFn, removeFn, setPage, setPageSize };
};
